/* Smart guides + snapping for dragging / resizing a visual on the grid.
 *
 * Everything is in grid cells. While dragging, the pointer delta is a
 * fractional number of cells; without snapping it rounds to the nearest whole
 * cell (the existing grid snap). With snapping, if one of the moving edges /
 * the centre lies within `threshold` cells of another visual's edge / centre
 * or of the page margins / centre, the delta jumps so the two coincide.
 * Alt disables it (caller passes enabled=false). The result is always a whole
 * number of cells, so it composes with the existing grid snapping. */
import type { SLayout } from "./model";

export type DragHandle = "move" | "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

export interface Guide {
  /** "v" = vertical line at x=pos; "h" = horizontal line at y=pos (cells). */
  axis: "v" | "h";
  pos: number;
  /** Extent along the other axis (cells). */
  from: number;
  to: number;
}

export interface SnapArea {
  cols: number;
  rows: number;
}

/** Target lines on one axis: other visuals' start/centre/end plus the page's 0 / middle / end. */
export function targetLines(others: SLayout[], axis: "x" | "y", size: number): number[] {
  const set = new Set<number>([0, size / 2, size]);
  for (const o of others) {
    const a = axis === "x" ? o.x : o.y;
    const l = axis === "x" ? o.w : o.h;
    set.add(a);
    set.add(a + l / 2);
    set.add(a + l);
  }
  return [...set];
}

/** Best whole-cell delta that lands one of `anchors` (start positions) on a target line, or null. */
function bestDelta(anchors: number[], raw: number, lines: number[], threshold: number): number | null {
  let best: { d: number; err: number } | null = null;
  for (const a of anchors) {
    for (const t of lines) {
      const d = t - a;
      if (!Number.isInteger(d)) continue; // half-cell centre that cannot be reached on the grid
      const err = Math.abs(a + raw - t);
      if (err <= threshold && (!best || err < best.err)) best = { d, err };
    }
  }
  return best ? best.d : null;
}

/**
 * Whole-cell pointer deltas {dc, dr} for a move / resize drag.
 * `rawC` / `rawR` are the fractional pointer deltas in cells.
 */
export function snapDelta(opts: { start: SLayout; handle: DragHandle; rawC: number; rawR: number; others: SLayout[]; area: SnapArea; threshold: number; enabled: boolean }): { dc: number; dr: number } {
  const { start: s, handle, rawC, rawR, others, area, threshold, enabled } = opts;
  let dc = Math.round(rawC);
  let dr = Math.round(rawR);
  if (!enabled) return { dc, dr };
  const lx = targetLines(others, "x", area.cols);
  const ly = targetLines(others, "y", area.rows);
  let xs: number[] = [];
  let ys: number[] = [];
  if (handle === "move") {
    xs = [s.x, s.x + s.w / 2, s.x + s.w];
    ys = [s.y, s.y + s.h / 2, s.y + s.h];
  } else {
    if (handle.includes("e")) xs = [s.x + s.w];
    if (handle.includes("w")) xs = [s.x];
    if (handle.includes("s")) ys = [s.y + s.h];
    if (handle.includes("n")) ys = [s.y];
  }
  if (xs.length) dc = bestDelta(xs, rawC, lx, threshold) ?? dc;
  if (ys.length) dr = bestDelta(ys, rawR, ly, threshold) ?? dr;
  return { dc, dr };
}

/** The guide lines to draw for a layout: every edge / centre that coincides with a target line. */
export function guidesFor(rect: SLayout, others: SLayout[], area: SnapArea): Guide[] {
  const out: Guide[] = [];
  const add = (axis: "v" | "h", pos: number) => {
    const spanOf = (r: SLayout) => (axis === "v" ? [r.y, r.y + r.h] : [r.x, r.x + r.w]);
    const involved = others.filter((o) => {
      const a = axis === "v" ? [o.x, o.x + o.w / 2, o.x + o.w] : [o.y, o.y + o.h / 2, o.y + o.h];
      return a.includes(pos);
    });
    const spans = [spanOf(rect), ...involved.map(spanOf)];
    if (!out.some((x) => x.axis === axis && x.pos === pos)) out.push({ axis, pos, from: Math.min(...spans.map((x) => x[0])), to: Math.max(...spans.map((x) => x[1])) });
  };
  const lx = new Set(targetLines(others, "x", area.cols));
  const ly = new Set(targetLines(others, "y", area.rows));
  for (const x of [rect.x, rect.x + rect.w / 2, rect.x + rect.w]) if (lx.has(x)) add("v", x);
  for (const y of [rect.y, rect.y + rect.h / 2, rect.y + rect.h]) if (ly.has(y)) add("h", y);
  return out;
}
